import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import routesConfig from "../routesConfig";
import { useDispatch, useSelector } from "react-redux";
import { resetUser } from "../features/user/userSlice";
import {
  Collapse,
  List,
  ListItemButton,
  ListItemText,
  Paper,
} from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { AppDispatch, RootState } from "../store";
import { useTranslation } from "react-i18next";
import {
  canAccessPathWithoutOpenAccount,
  isAgentBlockedByAccountStatus,
} from "../utils/accountAccess";

const routeLabelMap: Record<string, string> = {
  "/agents": "menu.agents",
  "/rates": "menu.rates",
  "/clients_monthly_adjustments": "menu.monthlyAdjustments",
  "/mydata": "menu.myData",
  "/change_password": "menu.changePassword",
  "/detailing": "menu.detailing",
  "/leads": "menu.leads",
  "/s2s": "menu.s2s",
  "/s2s/create": "menu.s2sCreate",
  "/s2s/manage": "menu.s2sManage",
  "/s2s/conformation": "menu.s2sConfirmation",
  "/my_links": "menu.myLinks",
  "/documents": "menu.documents",
  // "/training": "menu.training",
};

const LeftMenuNew = () => {
  const dispatch: AppDispatch = useDispatch();
  const { t } = useTranslation();

  const [openMenus, setOpenMenus] = useState<any>({});
  const location = useLocation();
  const navigate = useNavigate();
  const userRoles = useSelector((state: RootState) => state.user.roles);
  const user = useSelector((state: RootState) => state.user.user);
  const provider = useSelector((state: RootState) => state.app.provider);
  const isBlockedByAccountStatus = isAgentBlockedByAccountStatus(
    user,
    userRoles,
  );

  const availableRoutes = routesConfig.filter(
    (route) =>
      route.requiredRoles.some((role) => userRoles.includes(role)) &&
      (!route.requiredProviders ||
        route.requiredProviders.length === 0 ||
        route.requiredProviders.includes(provider)) &&
      (!isBlockedByAccountStatus ||
        canAccessPathWithoutOpenAccount(route.path)),
  );

  const getRouteLabel = (path: string, fallback: string) => {
    const key = routeLabelMap[path];

    return key ? t(key) : fallback;
  };

  const toggleSubMenu = (path: any, componentPath: any) => {
    setOpenMenus((prev: { [x: string]: any }) => ({
      ...prev,
      [path]: !prev[path],
    }));
    if (componentPath) {
      navigate(path);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    dispatch(resetUser());
    navigate("/");
  };

  return (
    <Paper sx={{ width: "100%", maxWidth: 260, bgcolor: "background.paper" }}>
      <List component="nav">
        {availableRoutes.map((route) => {
          const isActive = location.pathname === route.path;
          const isOpen = openMenus?.[route.path] ?? false;

          return (
            <div key={route.path}>
              <ListItemButton
                onClick={() => toggleSubMenu(route.path, route.componentPath)}
                selected={isActive}
                id={"nav-" + route.path}
              >
                <ListItemText
                  primary={getRouteLabel(route.path, route.label)}
                />
                {route.children && (isOpen ? <ExpandLess /> : <ExpandMore />)}
              </ListItemButton>

              {route.children && (
                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {route.children
                      .filter((subRoute) =>
                        subRoute.requiredRoles.some((role) =>
                          userRoles.includes(role),
                        ),
                      )
                      .map((subRoute) => (
                        <ListItemButton
                          key={subRoute.path}
                          sx={{ pl: 4 }}
                          selected={location.pathname === subRoute.path}
                          component={Link}
                          to={subRoute.path}
                          id={"nav-" + subRoute.path}
                        >
                          <ListItemText
                            primary={getRouteLabel(
                              subRoute.path,
                              subRoute.label,
                            )}
                          />
                        </ListItemButton>
                      ))}
                  </List>
                </Collapse>
              )}
            </div>
          );
        })}

        <ListItemButton onClick={handleLogout}>
          <ListItemText primary={t("common.logout")} />
        </ListItemButton>
      </List>
    </Paper>
  );
};

export default LeftMenuNew;
